import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Select, Tooltip, Checkbox, Segmented } from 'antd';
import { InfoCircleOutlined, QuestionCircleOutlined, DownOutlined } from '@ant-design/icons';
import { getTargetLanguageFromPair } from '../utils/languagePairs';

type DemoExportBookModalProps = {
  visible: boolean;
  onClose: () => void;
  onExport?: (params: { version: string; format: string; includeImages: boolean; writingOrientation: 'horizontal' | 'vertical' }) => void;
  uploadType?: string; // 'Word' | 'ePub' (case-insensitive)
  translationPairs?: string[]; // e.g., ['English-German', 'English-Spanish']
  chapters?: { order: number }[];
  translatedChaptersByLang?: Record<string, number[]>;
};

const DemoExportBookModal: React.FC<DemoExportBookModalProps> = ({
  visible,
  onClose,
  onExport,
  uploadType = 'Word',
  translationPairs = [],
  chapters = [],
  translatedChaptersByLang = {},
}) => {
  const normalizedUploadType = useMemo(() => {
    const u = (uploadType || '').toLowerCase();
    if (u === 'epub' || u === 'epub document' || u === 'epub2' || u === 'epub3') return 'ePub';
    return 'Word';
  }, [uploadType]);

  const [exportVersion, setExportVersion] = useState<string>('Original');
  const [exportFormat, setExportFormat] = useState<string>(normalizedUploadType === 'ePub' ? 'ePub' : 'Word');
  const [includeImages, setIncludeImages] = useState<boolean>(true);
  const [disableExport, setDisableExport] = useState<boolean>(false);
  const [writingOrientation, setWritingOrientation] = useState<'horizontal' | 'vertical'>('horizontal');

  useEffect(() => {
    if (!visible) {
      // reset when closing
      setExportVersion('Original');
      setExportFormat(normalizedUploadType === 'ePub' ? 'ePub' : 'Word');
      setIncludeImages(true);
      setDisableExport(false);
      setWritingOrientation('horizontal');
    }
  }, [visible, normalizedUploadType]);

  const handleExport = () => {
    onExport?.({
      version: exportVersion,
      format: exportFormat,
      includeImages,
      writingOrientation,
    });
  };

  const targetLanguage = useMemo(() => {
    if (!exportVersion || exportVersion === 'Original') return '';
    return getTargetLanguageFromPair(exportVersion) || '';
  }, [exportVersion]);

  const LANGS_TO_ENFORCE = new Set(['German', 'French']);
  const isJapaneseTarget = targetLanguage === 'Japanese';

  const expectedOrders = useMemo(() => (chapters || []).map(c => c.order).sort((a,b)=>a-b), [chapters]);
  const allTranslatedForTarget = useMemo(() => {
    if (!targetLanguage || !LANGS_TO_ENFORCE.has(targetLanguage)) return true; // only enforce for these demo langs
    if (!expectedOrders || expectedOrders.length === 0) return true; // nothing to enforce
    const done = new Set((translatedChaptersByLang?.[targetLanguage] || []));
    return expectedOrders.every(o => done.has(o));
  }, [targetLanguage, expectedOrders, translatedChaptersByLang]);

  useEffect(() => {
    setDisableExport(!(exportVersion === 'Original' || allTranslatedForTarget));
  }, [exportVersion, allTranslatedForTarget]);

  useEffect(() => {
    if (!isJapaneseTarget && writingOrientation !== 'horizontal') {
      setWritingOrientation('horizontal');
    }
  }, [isJapaneseTarget, writingOrientation]);

  const showGermanWarning = exportVersion !== 'Original' && exportVersion.toLowerCase().includes('german');
  const showIncompleteWarning = exportVersion !== 'Original' && !!targetLanguage && LANGS_TO_ENFORCE.has(targetLanguage) && !allTranslatedForTarget;

  return (
    <Modal
      title={
        <>
          Export Book
          {exportFormat === 'ePub' && (
            <Tooltip title="Any chapters from the original ePub that were not translated by Scribeshadow will remain in the ePub in their original form.">
              <QuestionCircleOutlined style={{ marginLeft: '8px', fontSize: '16px' }} />
            </Tooltip>
          )}
        </>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="export" type="primary" onClick={handleExport} disabled={disableExport} data-testid="tutorial:export:start">
          Export
        </Button>,
      ]}
    >
      <div data-testid="tutorial:export:modal">
      {/* Version */}
      <div data-testid="tutorial:export:version" style={{ marginBottom: '10px' }}>
        <label style={{ marginRight: '10px' }}>Version:</label>
        <Select
          value={exportVersion}
          onChange={(v) => setExportVersion(v)}
          style={{ width: '100%' }}
          listHeight={400}
          suffixIcon={
            <span className="ant-select-suffix">
              <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
            </span>
          }
        >
          <Select.Option value="Original">Original</Select.Option>
          <Select.Option value="Proofread" disabled>
            Original (Proofread) (Unavailable in demo)
          </Select.Option>
          {translationPairs.map((pair) => (
            <Select.Option key={pair} value={pair}>
              {pair.replace('-', ' to ')}
            </Select.Option>
          ))}
        </Select>
      </div>

      {/* Format */}
      <div data-testid="tutorial:export:format">
        <label style={{ marginRight: '10px' }}>
          Format:{' '}
          <Tooltip title="Scribeshadow can export books uploaded as Word documents only as Word documents. Books uploaded as ePubs can be exported as ePubs or Word documents. Please note that the ePub to Word conversion may lose some formatting.">
            <QuestionCircleOutlined style={{ cursor: 'help' }} />
          </Tooltip>
        </label>
        <br />
        <Select
          value={exportFormat}
          onChange={setExportFormat}
          style={{ width: '100%' }}
          suffixIcon={
            <span className="ant-select-suffix">
              <DownOutlined style={{ color: '#1677ff', pointerEvents: 'none' }} />
            </span>
          }
        >
          {normalizedUploadType === 'Word' && <Select.Option value="Word">Word</Select.Option>}
          {normalizedUploadType === 'ePub' && <Select.Option value="ePub">ePub</Select.Option>}
          {normalizedUploadType === 'ePub' && <Select.Option value="Word">Word</Select.Option>}
        </Select>
      </div>

      {/* Include Images checkbox for ePub -> Word */}
      {normalizedUploadType === 'ePub' && exportFormat === 'Word' && (
        <div data-testid="tutorial:export:include-images" style={{ marginTop: '15px' }}>
          <Checkbox checked={includeImages} onChange={(e) => setIncludeImages(e.target.checked)}>
            Include images from ePub embedded in Word document
          </Checkbox>
        </div>
      )}

      {isJapaneseTarget && (exportFormat === 'Word' || exportFormat === 'ePub') && (
        <div style={{ marginTop: '15px' }}>
          <label style={{ marginRight: '10px' }}>Japanese Text Orientation:</label>
          <Segmented
            size="small"
            value={writingOrientation}
            onChange={(value) => setWritingOrientation(value as 'horizontal' | 'vertical')}
            options={[
              { label: 'Horizontal (横書き)', value: 'horizontal' },
              { label: 'Vertical (縦書き)', value: 'vertical' },
            ]}
          />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            Applies to this export only.
          </div>
        </div>
      )}

      {/* German language warning */}
      {showGermanWarning && (
        <div
          data-testid="tutorial:export:warnings"
          style={{
            marginTop: '15px',
            padding: '10px',
            backgroundColor: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: '4px',
          }}
        >
          <p style={{ margin: 0 }}>
            <InfoCircleOutlined style={{ marginRight: '8px', color: '#faad14' }} />
            <strong>Important Note About Amazon's German Spellcheck:</strong>
            <br />
            Amazon's spellchecker often flags a large number of "errors," in German text. These are false positives and not actual spelling mistakes. This behavior stems from limitations in the spellchecker's handling of the German language.
          </p>
        </div>
      )}

      {/* Incomplete translation warning */}
      {showIncompleteWarning && (
        <div
          data-testid="tutorial:export:warnings"
          style={{
            marginTop: '15px',
            padding: '10px',
            backgroundColor: '#fff1f0',
            border: '1px solid #ffa39e',
            borderRadius: '4px',
          }}
        >
          <p style={{ margin: 0 }}>
            <InfoCircleOutlined style={{ marginRight: '8px', color: '#cf1322' }} />
            <strong>Export Disabled:</strong> Not all chapters are translated in {targetLanguage}. Please translate all chapters before exporting this language.
          </p>
        </div>
      )}
      </div>
    </Modal>
  );
};

export default DemoExportBookModal;
